import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useEffect
} from 'react';
import useLoadScript from '../../hooks/useLoadScript';

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCmTLrfcDvLCnrIMhhRHk80m3SIz3kUhP8' || '';

interface GoogleMapsContextProps {
  loaded: boolean;
  loadScript: (shouldLoad: boolean) => void;
}

interface GoogleMapsProviderProps {
  children: ReactNode;
}

export const GoogleMapsContext = createContext<GoogleMapsContextProps>({
  loaded: false,
  loadScript: (shouldLoad: boolean) => {
    console.warn('GoogleMapsContext used outside of GoogleMapsProvider');
  }
});

export const GoogleMapsProvider: FC<GoogleMapsProviderProps> = ({ children }) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [shouldLoadScript, setShouldLoadScript] = useState<boolean>(true);

  const loadScript = useCallback((shouldLoad: boolean) => {
    setShouldLoadScript(shouldLoad);
    if (!shouldLoad) {
      setLoaded(false);
    }
  }, []);

  const scriptUrl = shouldLoadScript
    ? `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=directions,places`
    : '';

  useLoadScript({
    resourceUrl: scriptUrl,
    element: 'body',
    async: true,
    id: 'google-maps',
    onLoad: () => {
      if (shouldLoadScript) {
        setLoaded(true);
      }
    }
  });

  return (
    <GoogleMapsContext.Provider value={{ loaded, loadScript }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

interface UseGoogleMapsOptions {
  loadScript?: boolean;
}

const useGoogleMaps = (options?: UseGoogleMapsOptions): GoogleMapsContextProps => {
  const context = useContext(GoogleMapsContext);

  useEffect(() => {
    if (options?.loadScript !== undefined) {
      context.loadScript(options.loadScript);
    }
  }, [context, options?.loadScript]);

  return context;
};

export default useGoogleMaps;
